'use client'

import { useEffect, useState } from 'react'
import { usePathname } from 'next/navigation'
import Link from 'next/link'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faHiveIconSmallLight } from '@awesome.me/kit-94e593bf23/icons/kit/custom'

const STORAGE_KEY = 'announcement_rotation'

// Module scope resets only on a full page load, so this distinguishes a real
// refresh (should advance the rotation) from a same-mount effect re-run like
// Strict Mode's double invoke (should not)
let hasRotatedThisPageLoad = false

function shuffle(items) {
    const result = [...items]
    for (let i = result.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1))
        ;[result[i], result[j]] = [result[j], result[i]]
    }
    return result
}

function readRotation() {
    try {
        return JSON.parse(sessionStorage.getItem(STORAGE_KEY))
    } catch {
        return null
    }
}

function saveRotation(rotation) {
    try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(rotation))
    } catch {
        // sessionStorage unavailable - rotation restarts on next mount
    }
}

const Announcements = ({ announcements, className = '' }) => {

    const pathname = usePathname()

    // Server render and hydration must agree, so both render the first
    // announcement - but invisible, so no one sees it before rotation runs.
    // The effect picks the real announcement and reveals it in one commit.
    const [announcement, setAnnouncement] = useState(announcements?.[0] ?? null)
    const [visible, setVisible] = useState(false)

    useEffect(() => {
        if (announcements && announcements.length >= 2) {
            const ids = announcements.map(a => a.announcement_id)
            const rotation = readRotation()

            const isCurrent = rotation
                && Array.isArray(rotation.order)
                && [...rotation.order].sort().join() === [...ids].sort().join()

            if ( ! isCurrent) {
                // First page view this session (or the announcement set
                // changed): shuffle a fresh order starting from the first
                // announcement, which is the one about to be revealed
                const order = shuffle(ids)
                order.splice(order.indexOf(ids[0]), 1)
                order.unshift(ids[0])
                saveRotation({ order, position: 0, pathname })
            } else if ( ! (hasRotatedThisPageLoad && rotation.pathname === pathname)) {
                // Advance unless this is a same-route re-run within the same
                // page load (Strict Mode double run, or announcements prop
                // identity change) - a real refresh resets the module flag,
                // so it advances even though the pathname matches
                rotation.position = (rotation.position + 1) % rotation.order.length
                rotation.pathname = pathname
                saveRotation(rotation)
                setAnnouncement(announcements.find(a => a.announcement_id === rotation.order[rotation.position]))
            }

            hasRotatedThisPageLoad = true
        }

        setVisible(true)
    }, [pathname, announcements])

    if ( ! announcement || ! announcement.announcement_id) {
        return null
    }

    return (
        <div role="region" aria-label="Site announcement" className={`w-full py-2 flex items-center justify-center bg-gray-2 border-b border-gray-8 lg:absolute lg:top-2.5 lg:left-1/2 lg:-translate-x-1/2 lg:w-auto lg:py-0 lg:bg-transparent lg:border-0 group-[.collapsed]/header:h-0 group-[.collapsed]/header:py-0 group-[.collapsed]/header:overflow-hidden ${className}`}>
            <Link href={announcement.announcement_url} className={`group flex items-center mt-[3px] transition-opacity duration-500 ${visible ? 'visible opacity-100' : 'invisible opacity-0'}`}>
                <FontAwesomeIcon icon={faHiveIconSmallLight} aria-hidden="true" className="text-base text-primary group-hover:text-ink-muted mr-1" />
                <span className="text-2xs text-ink-muted font-medium leading-none lg:group-hover:text-primary">{announcement.announcement_title}</span>
            </Link>
        </div>
    )
}

export default Announcements
